<template>
  <b-modal
    ref="modal"
    :title="header"
    centered
    hide-footer
    size="md"
    static
    class="forgot-password-form"
    @shown="onShow"
  >
    <b-img
      slot="modal-header-close"
      :src="require('@/assets/icons/x.svg')"
      width="40"
      height="24"
    ></b-img>

    <p class="modal-sub-title text-left">
      {{ subheader }}
      <span>{{
        currentStep !== steps['verify'].value ? form.email : null
      }}</span>
    </p>

    <b-form class="form" onkeydown="return event.key != 'Enter'">
      <b-form-group v-if="currentStep === steps['verify'].value">
        <custom-input
          ref="email"
          type="email"
          placeholder="Email Address"
          v-model="form.email"
          autofocus
          required
          :disabled="form.$busy"
          :is-invalid="form.$getError('email')"
          @change="isEmail"
          @keyup.enter="nextStep"
        ></custom-input>
      </b-form-group>
      <b-form-group v-else-if="currentStep === steps['code'].value">
        <code-input
          ref="code"
          v-model="form.code"
          :loading="form.$busy"
          :field-width="42.5"
          :field-height="42.5"
          :is-invalid="form.$getError('token')"
          @complete="nextStep"
        ></code-input>
      </b-form-group>
      <b-form-group v-else>
        <custom-input
          ref="password"
          :type="showPassword ? 'text' : 'password'"
          placeholder="New Password"
          v-model="form.password"
          autofocus
          :disabled="form.$busy"
          :is-invalid="form.$getError('password')"
          @keyup.enter="nextStep"
        >
          <template slot="icon">
            <b-icon
              v-if="!showPassword"
              class="show"
              icon="eye"
              aria-hidden="true"
              @click="togglePassword(true)"
            ></b-icon>
            <b-icon
              v-else
              class="hide"
              icon="eye-slash"
              aria-hidden="true"
              @click="togglePassword(false)"
            ></b-icon>
          </template>
        </custom-input>
      </b-form-group>

      <error-message
        class="mb-2"
        sign=""
        :error="
          form.$getError('email') ||
            form.$getError('token') ||
            form.$getError('password') ||
            form.$errorMessage
        "
      ></error-message>

      <div class="modal-footer">
        <div>
          <resend-verification-code-form
            v-if="currentStep === steps['code'].value"
            :form="form"
            to="forgot"
            class="forgot-btn float-left mt-3"
          >
          </resend-verification-code-form>
        </div>
        <div class="modal__btns">
          <b-button variant="outline-secondary" @click="close()"
            >Cancel</b-button
          >
          <b-button variant="primary" :disabled="form.$busy" @click="nextStep">
            <b-spinner small v-if="form.$busy"></b-spinner>&nbsp;{{
              buttonText
            }}
          </b-button>
        </div>
      </div>
    </b-form>
  </b-modal>
</template>
<script>
/**
 * ==================================================================================
 * Forgot Password form
 * ==================================================================================
 **/

import { mapState, mapActions } from 'vuex'
import IsEmail from 'isemail'
import Form from '@/utils/form'
import CustomInput from '@/components/inputs/CustomInput'
import ErrorMessage from '@/components/inputs/ErrorMessage'
import CodeInput from '@/components/inputs/CodeInput'
import ResendVerificationCodeForm from '@/components/forms/ResendVerificationCodeForm'

export default {
  components: {
    CustomInput,
    ErrorMessage,
    CodeInput,
    ResendVerificationCodeForm
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    })
  },
  data: function() {
    return {
      showPassword: false,
      form: new Form({
        email: null,
        password: null,
        code: null
      }),

      header: null,
      subheader: null,
      buttonText: null,
      currentStep: null,
      steps: {
        verify: {
          value: 1,
          header: 'Password Reset',
          subheader:
            'Enter your email address to receive a code to reset your password',
          buttonText: 'Continue'
        },
        code: {
          value: 2,
          header: 'Verification Code',
          subheader: 'Input the code sent to: ',
          buttonText: 'Continue'
        },
        new: {
          value: 3,
          header: 'New Password',
          subheader: 'Enter new password to log in ',
          buttonText: 'Save'
        }
      }
    }
  },
  methods: {
    ...mapActions({
      requestForgotPassword: 'auth/requestForgotPassword',
      checkForgotPassword: 'auth/checkForgotPassword',
      verifyForgotPassword: 'auth/verifyForgotPassword',
      addAlert: 'alert/addAlert'
    }),

    nextStep() {
      if (this.form.$busy) return
      this.form.$setLoading(true)
      switch (this.currentStep) {
        case this.steps['verify'].value:
          return this.changePasswordRequest()
        case this.steps['code'].value:
          return this.verifyCode()
        case this.steps['new'].value:
          return this.changePassword()
      }
    },

    async changePasswordRequest() {
      await this.requestForgotPassword({
        email: this.form.email
      })
        .then(() => {
          this.form.$setLoading(false)
          this.setStep(this.steps['code'])
          this.setFocus('code')
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.setFocus('email')
        })
    },

    async verifyCode() {
      await this.checkForgotPassword({
        username: this.form.email,
        token: this.form.code
      })
        .then(() => {
          this.form.$setLoading(false)
          this.setStep(this.steps['new'])
          this.setFocus('password')
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.setFocus('code')
        })
    },

    async changePassword() {
      await this.verifyForgotPassword({
        username: this.form.email,
        token: this.form.code,
        password: this.form.password,
        password_confirmation: this.form.password
      })
        .then(() => {
          this.addAlert({
            label: 'Your password has been updated'
          })
          this.reset()
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.setFocus('password')
        })
    },

    togglePassword(bool) {
      this.showPassword = bool
    },

    setStep(obj) {
      this.currentStep = obj.value
      this.header = obj.header
      this.subheader = obj.subheader
      this.buttonText = obj.buttonText
    },

    setFocus(ref) {
      this.$nextTick(() => {
        if (!this.$refs[ref]) return
        this.$refs[ref].focus()
      })
    },

    isEmail(val) {
      if (!IsEmail.validate(val)) {
        this.form.$setError('email', 'Must be a valid email address')
      } else {
        this.form.$clearError('email')
      }
    },

    reset() {
      this.close()
      this.setStep(this.steps['verify'])
      this.form.email = null
      this.form.password = null
      this.form.code = null
      this.form.setLoading(false)
    },

    show() {
      this.setStep(this.steps['verify'])
      this.$refs.modal.show()
    },

    close() {
      this.$refs.modal.hide()
    },

    onShow() {
      this.setFocus('email')
      this.form.email = this.user ? this.user.email : null
    }
  },

  created() {
    this.setStep(this.steps['verify'])
  }
}
</script>
<style scoped lang="scss">
.forgot-password-form /deep/ {
  .error-label {
    margin-top: 0;
  }

  .forgot-btn {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--brand-green);
    cursor: pointer;
  }

  .form-group:first-child {
    margin-top: 30px;
  }

  .modal-dialog {
    max-width: 520px;
  }

  .modal-content {
    padding: 10px 20px 20px 30px;
  }

  .modal-header {
    padding: 1rem 0 0 0;

    .close {
      margin-right: -30px;
    }
  }

  .modal-body {
    padding: 0;

    .modal-sub-title {
      margin-top: 10px;
      margin-bottom: 20px;

      span {
        font-weight: 900;
      }
    }
  }

  .modal-footer {
    padding-top: 20px;
    justify-content: space-between;

    .btn {
      line-height: 27px;
      min-width: 125px;
      padding: 0.6rem 0.75rem;
    }
  }
}

@media (max-width: 768px) {
  .forgot-password-form /deep/ {
    .modal-content {
      padding-left: 20px;
    }
  }
}
</style>
